/* Lynqu links intentionally use rel="noopener" WITHOUT "noreferrer": the
   referrer header must reach lynqu.com so the backlink registers as attributed
   referral traffic (and stays a dofollow link for SEO). noopener already
   neutralises the window.opener security concern. */
/* eslint-disable react/jsx-no-target-blank */
import "../../styles/lynqu.css";
import LynquWordmark from "../../assets/lynqu/lynqu-wordmark-dark.webp";
import LynquPhone from "../../assets/lynqu/liara-dark-iphone.webp";
import StoreBadges from "../ui/StoreBadges";

const LYNQU_URL = "https://lynqu.com";

// Capabilities that signal a networking + lead-capture platform — not just cards.
const FEATURES = ["Lead capture", "Smart cards", "CRM-ready"];

// Shared focus ring for keyboard users (a11y — High severity).
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A6B2FF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#08080c]";

function ArrowUpRight({ className }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      width='18'
      height='18'
      fill='none'
      stroke='currentColor'
      strokeWidth='2.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'>
      <path d='M7 17 17 7' />
      <path d='M7 7h10v10' />
    </svg>
  );
}

export default function LynquProduct(props) {
  const inView = props.inView;

  /* Staggered reveal: one transition, only the delay changes.
   * `transition-[opacity,transform]`, not `transition-all` — the latter
   * animates every animatable property, layout ones included.
   * The observer feeding `inView` is triggerOnce, so an entrance plays once
   * instead of replaying every time the section scrolls back into view. */
  const reveal = (delay) => ({
    className: `transition-[opacity,transform] duration-700 ease-out ${
      inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
    }`,
    style: { transitionDelay: `${delay}ms` },
  });

  return (
    <section
      id='product'
      ref={props.sectionRef}
      className='lynqu-section relative w-full min-h-screen overflow-hidden bg-[#08080c] flex items-center'>
      {/* Atmosphere: indigo glow mesh + faint grid (no CSS filter — keeps glass safe) */}
      <div className='pointer-events-none absolute inset-0 lynqu-mesh' />
      <div className='pointer-events-none absolute inset-0 lynqu-grid opacity-70' />
      <div className='pointer-events-none absolute inset-0 lynqu-grain' />

      <div ref={props.revealRef} className='relative z-10 w-full max-w-[1500px] mx-auto px-[28px] nav-sm:px-[40px] c-lg:px-[80px] c-xl:px-[112px] py-[96px] nav-sm:py-0 flex flex-col nav-sm:flex-row items-center gap-[48px] c-lg:gap-[80px]'>
        {/* ---- Copy column ---- */}
        <div className='w-full nav-sm:w-[55%] order-2 nav-sm:order-1'>
          {/* Headline — narrative bridge from the confidential-projects section */}
          <h2
            {...reveal(80)}
            className={`${reveal(80).className} text-white font-bold leading-[1.05] text-36 c-md:text-42 c-lg:text-50 c-xl:text-56`}>
            Our client work stays private.
            <br className='hidden c-md:block' />{" "}
            <span className='text-[#8996FF]'>This one doesn&apos;t.</span>
          </h2>

          {/* Brand wordmark — dofollow brand-anchor link */}
          <a
            {...reveal(160)}
            href={LYNQU_URL}
            target='_blank'
            rel='noopener'
            aria-label='Lynqu — visit lynqu.com'
            className={`${reveal(160).className} ${FOCUS_RING} group mt-[34px] inline-block rounded-md`}>
            <img
              src={LynquWordmark}
              alt='Lynqu'
              width={540}
              height={206}
              className='h-[44px] c-md:h-[56px] c-lg:h-[68px] w-auto transition-transform duration-300 group-hover:translate-x-[2px]'
            />
          </a>

          {/* New positioning: networking + lead-capture, not "just a business card" */}
          <p
            {...reveal(220)}
            className={`${reveal(220).className} mt-[20px] max-w-[520px] text-white font-semibold text-16 c-md:text-20 c-xl:text-22 leading-[1.4]`}>
            Share who you are. Capture who you meet.{" "}
            <span className='text-[#A6B2FF]'>Never lose a contact.</span>
          </p>

          {/* Feature chips */}
          <div
            {...reveal(280)}
            className={`${reveal(280).className} mt-[28px] flex flex-wrap gap-[10px]`}>
            {FEATURES.map((f) => (
              <span
                key={f}
                className='rounded-full border border-[#ffffff1f] bg-[#ffffff0d] backdrop-blur-sm px-[16px] py-[8px] text-13 c-lg:text-14 text-[#E7E5EE]'>
                {f}
              </span>
            ))}
          </div>

          {/* CTA — primary dofollow link to lynqu.com */}
          <div
            {...reveal(380)}
            className={`${reveal(380).className} mt-[36px] flex items-center gap-[18px]`}>
            <a
              href={LYNQU_URL}
              target='_blank'
              rel='noopener'
              className={`lynqu-cta ${FOCUS_RING} group inline-flex items-center gap-[9px] rounded-full px-[28px] py-[15px] min-h-[48px] text-15 c-lg:text-16 font-semibold text-white`}>
              Explore Lynqu
              <ArrowUpRight className='transition-transform duration-300 group-hover:translate-x-[3px] group-hover:-translate-y-[3px]' />
            </a>
            <a
              href={LYNQU_URL}
              target='_blank'
              rel='noopener'
              className={`${FOCUS_RING} inline-flex items-center min-h-[44px] rounded-md px-[6px] text-14 c-lg:text-15 text-[#9c98ad] hover:text-[#A6B2FF] transition-colors duration-200 underline-offset-4 hover:underline`}>
              lynqu.com
            </a>
          </div>

          {/* Store badges — the download band that used to follow this
              section now lives here, so the page stays one screen shorter. */}
          <div
            {...reveal(460)}
            className={`${reveal(460).className} mt-[36px] pt-[28px] border-t border-white/[0.08]`}>
            <p className='text-12 c-lg:text-13 font-semibold tracking-[0.18em] uppercase text-[#9c98ad]'>
              Get the app
            </p>
            <StoreBadges focusRing={FOCUS_RING} className='mt-[14px]' />
          </div>
        </div>

        {/* ---- Device column ---- */}
        <div className='w-full nav-sm:w-[45%] order-1 nav-sm:order-2 flex items-center justify-center relative'>
          {/* Radial glow behind the device */}
          <div className='pointer-events-none absolute w-[78%] aspect-square rounded-full lynqu-device-glow lynqu-glow-pulse' />
          <div
            className={`relative transition-[opacity,transform] duration-1000 ease-out ${
              inView ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-95"
            }`}>
            <img
              src={LynquPhone}
              alt='A Lynqu profile with Save Contact and social links on iPhone'
              width={474}
              height={1044}
              className='lynqu-float relative z-10 w-[240px] c-md:w-[290px] c-lg:w-[320px] c-xl:w-[348px] max-w-full max-h-[72vh] object-contain drop-shadow-[0_40px_80px_rgba(0,0,0,0.55)]'
              loading='lazy'
            />
          </div>
        </div>
      </div>
    </section>
  );
}
